import { computed, inject, Injector, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { AddressData, CommonAddressField, CommonAddressFieldConfig, CommonForm, CommonOptionsFieldConfig, CommonPhoneField, CommonPhoneFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { Person } from "@interfaces";
import { DomainDataService, FuncService } from "../../../../../services";

interface BasicInfoFields {
	firstName: string,
	middleInit: string | undefined,
	lastName: string,
	address: AddressData | undefined;
	contactMethodId: number | undefined,
	phone: string | undefined,
	phoneTypeId: number | undefined,
	doNotContactNote: string | undefined,
}

export class PersonBasicInfoForm {

	public readonly form = new CommonForm<BasicInfoFields>(inject(Injector));
	private domainService = inject(DomainDataService);
	private func = inject(FuncService);
	public values = signal<BasicInfoFields>({ firstName: '', middleInit: undefined, lastName: '', address: undefined, contactMethodId: 0, phoneTypeId: 1, phone: undefined, doNotContactNote: undefined });
	public readonly doNotContactFlag = signal<boolean>(false);

	constructor() {
		toObservable(this.doNotContactNote().pendingValue).subscribe((pendingValues: string | null) => {
			this.setDoNotContactFlag(pendingValues);
		})
	}

	setValues(personDetails: Person) {
		this.form.resetAll();
		this.values.set({
			firstName: personDetails.firstName,
			middleInit: personDetails.middleInit,
			lastName: personDetails.lastName,
			address: personDetails.placeAddress ? {
				googlePlaceKey: personDetails.googlePlaceKey,
				placeAddress: personDetails.placeAddress,
				stateCode: personDetails.stateCode,
				latitude: personDetails.latitude,
				longitude: personDetails.longitude,
				zipId: personDetails.zipId,
				countyFipsId: personDetails.countyFipsId,
				streetAddress: personDetails.placeAddress,
			} : undefined,
			contactMethodId: personDetails.contactMethodId,
			phoneTypeId: personDetails.phoneTypeId,
			phone: personDetails.phone,
			doNotContactNote: personDetails.doNotContactNote
		});
		this.setDoNotContactFlag(personDetails.doNotContactNote);
	}

	// null from form , undefined from DB
	setDoNotContactFlag(value: string | null | undefined) {
		this.doNotContactFlag.set(value && value.length ? true : false);
	}

	readonly firstName = computed<CommonTextField>(() => ({
		config: signal({
			label: 'First Name',
			isRequired: true,
			min: 2,
			max: 20,
			fixCase: 'sentence-case',
			multiLine: false,
		}),
		formFieldName: 'firstName',
		form: this.form,
		actualValue: computed(() => this.values().firstName),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly middleInit = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Middle Initial',
			isRequired: false,
			min: 0,
			max: 1,
			fixCase: 'upper-case',
			multiLine: false,
		}),
		formFieldName: 'middleInit',
		form: this.form,
		actualValue: computed(() => this.values().middleInit || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly lastName = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Last Name',
			isRequired: true,
			min: 2,
			max: 20,
			fixCase: 'sentence-case',
			multiLine: false,
		}),
		formFieldName: 'lastName',
		form: this.form,
		actualValue: computed(() => this.values().lastName),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly address = computed<CommonAddressField>(() => ({
		config: signal<CommonAddressFieldConfig>({
			label: 'Address',
			isRequired: false,
			funcGetCountyFipsId: this.func.address.getCountyFipsId,
		}),
		formFieldName: 'address',
		form: this.form,
		actualValue: computed(() => this.values()?.address),
		pendingValue: signal<AddressData | undefined | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

	private readonly preferredContactOptions = computed(() => {
		const domainData = this.domainService.data();
		return domainData.contactMethods.map(type => (
			{ text: type.name, value: type.contactMethodId, }
		)).sort((a, b) => a.text < b.text ? -1 : 1);
	});
	readonly preferredContact = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Preferred contact method',
			isRequired: false,
			options: this.preferredContactOptions(),
		}),
		formFieldName: 'contactMethodId',
		form: this.form,
		actualValue: computed(() => this.values().contactMethodId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	private readonly phoneTypeOptions = computed(() => {
		const domainData = this.domainService.data();
		return domainData.phoneTypes.map(type => (
			{ text: type.name, value: type.phoneTypeId, }
		))
	});
	readonly phoneType = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Phone Type',
			isRequired: false,
			options: this.phoneTypeOptions(),
		}),
		formFieldName: 'phoneTypeId',
		form: this.form,
		actualValue: computed(() => this.values().phoneTypeId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly phone = computed<CommonPhoneField>(() => ({
		config: signal<CommonPhoneFieldConfig>({
			label: 'Phone',
			isRequired: false,
		}),
		formFieldName: 'phone',
		form: this.form,
		actualValue: computed(() => this.values().phone || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly doNotContactNote = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Do Not Contact Note',
			min: 0,
			fixCase: 'sentence-case',
			isRequired: false,
			multiLine: true,
		}),
		formFieldName: 'doNotContactNote',
		form: this.form,
		actualValue: computed(() => this.values().doNotContactNote || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

}